import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Container
} from '@mui/material';
import {
  Add,
  Delete,
  Search,
  Business,
  LocalPharmacy,
} from '@mui/icons-material';
import { medicinesAPI, suppliersAPI, purchaseAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

export const NewPurchase = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [suppliers, setSuppliers] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Fetch medicines based on selected supplier and search term
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (selectedSupplier) {
        searchMedicinesBySupplier();
      } else {
        setMedicines([]);
      }
    }, 10);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, selectedSupplier]);

  const fetchSuppliers = async () => {
    try {
      const response = await suppliersAPI.getAll();
      setSuppliers(response.data.data.suppliers);
    } catch (error) {
      enqueueSnackbar('Error fetching suppliers', { variant: 'error' });
    }
  };

  const searchMedicinesBySupplier = async () => {
    if (searchTerm.trim() === "") {
      setMedicines([]);
      return;
    }
    if (!selectedSupplier) {
      setMedicines([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await medicinesAPI.getBySupplier(selectedSupplier._id, {
        search: searchTerm,
        limit: 20
      });

      setMedicines(response.data.data.medicines || []);

      if (response.data.data.medicines.length === 0 && searchTerm.trim()) {
        enqueueSnackbar(`No medicines found for "${searchTerm}" from ${selectedSupplier.name}`, {
          variant: 'info',
          autoHideDuration: 3000
        });
      }
    } catch (error) {
      console.error('Search medicines by supplier error:', error);
      enqueueSnackbar('Error searching medicines', { variant: 'error' });
      setMedicines([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const addItem = (medicine) => {
    const existingItem = items.find(item => item.medicine._id === medicine._id);

    if (existingItem) {
      setItems(items.map(item =>
        item.medicine._id === medicine._id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      // Use supplier's cost price and min order quantity
      const supplierCostPrice = medicine.supplierDetails?.costPrice || 0;
      const minOrderQuantity = medicine.supplierDetails?.minOrderQuantity || 1;

      setItems([...items, {
        medicine,
        quantity: minOrderQuantity,
        costPrice: supplierCostPrice,
        sellingPrice: 0, // Will be set by user
        mrp: 0, // Will be set by user
        taxRate: 18,
        minOrderQuantity: minOrderQuantity,
      }]);
    }

    setSearchTerm('');
    enqueueSnackbar(`${medicine.name} added to order`, { variant: 'success' });
  };

  const removeItem = (medicineId) => {
    setItems(items.filter(item => item.medicine._id !== medicineId));
  };

  const updateItem = (medicineId, field, value) => {
    setItems(items.map(item =>
      item.medicine._id === medicineId
        ? { ...item, [field]: parseFloat(value)  }
        : item
    ));
  };

  const updateQuantity = (medicineId, quantity) => {
    const item = items.find(item => item.medicine._id === medicineId);
    const minOrderQuantity = item?.minOrderQuantity || 1;

    if (quantity < minOrderQuantity) {
      enqueueSnackbar(`Minimum order quantity is ${minOrderQuantity}`, { variant: 'warning' });
      return;
    }

    if (quantity < 1) {
      removeItem(medicineId);
      return;
    }

    setItems(items.map(item =>
      item.medicine._id === medicineId
        ? { ...item, quantity }
        : item
    ));
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity *  (item.costPrice ? item.costPrice : 0)), 0);
    const tax = items.reduce((sum, item) => sum + (item.quantity * (item.costPrice ? item.costPrice : 0) * ((item.taxRate  ? item.taxRate : 0 )/ 100)), 0);
    const grandTotal = subtotal + tax;

    return { subtotal, tax, grandTotal };
  };

  const handleCreateOrder = async () => {
    if (!selectedSupplier) {
      enqueueSnackbar('Please select a supplier', { variant: 'warning' });
      return;
    }

    if (items.length === 0) {
      enqueueSnackbar('Please add items to the order', { variant: 'warning' });
      return;
    }

    // Validate all items have required fields
    const invalidItems = items.filter(item =>
      item.costPrice <= 0 || item.sellingPrice <= 0 || item.mrp <= 0
    );

    if (invalidItems.length > 0) {
      enqueueSnackbar('Please set valid prices for all items', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        supplier: selectedSupplier._id,
        items: items.map(item => ({
          medicine: item.medicine._id,
          quantity: item.quantity,
          costPrice: item.costPrice,
          sellingPrice: item.sellingPrice,
          mrp: item.mrp,
          taxRate: item.taxRate,
        })),
        expectedDeliveryDate: expectedDelivery || undefined,
        notes: notes || undefined,
      };

      await purchaseAPI.create(orderData);
      enqueueSnackbar('Purchase order created successfully!', { variant: 'success' });
      navigate('/hospital/pharmacy/purchases');
    } catch (error) {
      enqueueSnackbar('Error creating purchase order', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierChange = (event, newValue) => {
    setSelectedSupplier(newValue);
    setSearchTerm('');
    setMedicines([]);
    setItems([]);
  };

  const { subtotal, tax, grandTotal } = calculateTotals();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box>
          <Typography variant="h3" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mb: 4 }}>
            New Purchase Order
          </Typography>

          <Grid container spacing={3}>
            {/* Left Column - Supplier & Medicine Search */}
            <Grid item xs={12} md={8}>
              {/* Supplier Selection */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    Supplier Information
                  </Typography>
                  <Autocomplete
                    options={suppliers}
                    getOptionLabel={(option) =>
                      `${option.name} (${option.contactPerson})`
                    }
                    value={selectedSupplier}
                    onChange={handleSupplierChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Supplier"
                        placeholder="Search supplier by name"
                        sx={{ fontSize: '1.1rem' }}
                        InputProps={{
                          ...params.InputProps,
                          sx: { fontSize: '1.1rem' },
                          startAdornment: (
                            <>
                              <Business sx={{ mr: 1, color: 'text.secondary', fontSize: 28 }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                      />
                    )}
                  />

                  {selectedSupplier && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        <strong>Contact:</strong> {selectedSupplier.contactPerson}
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        <strong>Phone:</strong> {selectedSupplier.phone}
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                        <strong>Email:</strong> {selectedSupplier.email}
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                        <strong>Lead Time:</strong> {selectedSupplier.leadTime} days
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Medicine Search */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    Add Medicines
                  </Typography>

                  {!selectedSupplier ? (
                    <Alert severity="info" sx={{ 
                      mb: 2, 
                      fontSize: '1.1rem',
                      '& .MuiAlert-message': { fontSize: '1.1rem' }
                    }}>
                      Please select a supplier first to search for medicines.
                    </Alert>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search medicines by name, generic name, or brand..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ fontSize: '1.1rem' }}
                        InputProps={{
                          sx: { fontSize: '1.1rem' },
                          startAdornment: (
                            <Search sx={{ mr: 1, color: 'text.secondary', fontSize: 28 }} />
                          ),
                          endAdornment: searchLoading && (
                            <CircularProgress size={24} />
                          ),
                        }}
                      />

                      {searchTerm && (
                        <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                          {searchLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                              <CircularProgress size={40} />
                            </Box>
                          ) : medicines.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                              <LocalPharmacy sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                              <Typography variant="h4" color="text.secondary" sx={{ fontSize: '1.3rem', mb: 1 }}>
                                No medicines found for "{searchTerm}"
                              </Typography>
                              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                                Try a different search term
                              </Typography>
                            </Box>
                          ) : (
                            medicines.map((medicine) => (
                              <Box
                                key={medicine._id}
                                sx={{
                                  p: 2,
                                  mb: 1,
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: 'action.hover' },
                                }}
                                onClick={() => addItem(medicine)}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                    {medicine.name}
                                  </Typography>
                                  <Chip
                                    label={`₹${medicine.supplierDetails?.costPrice || 0}`}
                                    size="medium"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '1rem' }}
                                  />
                                </Box>
                                <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontSize: '1rem' }}>
                                  {medicine.genericName} • {medicine.strength} • {medicine.form}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                  {medicine.brand} • {medicine.manufacturer}
                                </Typography>
                                {medicine.supplierDetails?.minOrderQuantity > 1 && (
                                  <Typography variant="h6" color="warning.main" sx={{ fontSize: '1rem', mt: 1 }}>
                                    Min. order: {medicine.supplierDetails.minOrderQuantity}
                                  </Typography>
                                )}
                              </Box>
                            ))
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Order Items */}
              <Card>
                <CardContent sx={{ py: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
                      Order Items
                    </Typography>
                    {items.length > 0 && (
                      <Chip
                        label={`${items.length} items`}
                        color="primary"
                        variant="outlined"
                        size="medium"
                        sx={{ fontSize: '1.1rem' }}
                      />
                    )}
                  </Box>

                  {items.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <LocalPharmacy sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h4" color="text.secondary" sx={{ fontSize: '1.3rem' }}>
                        {!selectedSupplier
                          ? 'Select a supplier to start adding medicines'
                          : 'No items in order. Search and add medicines above.'
                        }
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Medicine</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Quantity</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Cost Price</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Selling Price</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>MRP</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Tax Rate</TableCell>
                            <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Amount</TableCell>
                            <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((item) => (
                            <TableRow key={item.medicine._id}>
                              <TableCell sx={{ py: 2 }}>
                                <Box>
                                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                    {item.medicine.name}
                                  </Typography>
                                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                    {item.medicine.genericName}
                                  </Typography>
                                  {item.minOrderQuantity > 1 && (
                                    <Typography variant="h6" color="warning.main" sx={{ fontSize: '1rem', mt: 0.5 }}>
                                      Min: {item.minOrderQuantity}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                  <IconButton
                                    size="large"
                                    onClick={() => updateQuantity(item.medicine._id, item.quantity - 1)}
                                    disabled={item.quantity <= item.minOrderQuantity}
                                    sx={{ fontSize: '1.5rem' }}
                                  >
                                    -
                                  </IconButton>
                                  <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center', fontSize: '1.2rem' }}>
                                    {item.quantity}
                                  </Typography>
                                  <IconButton
                                    size="large"
                                    onClick={() => updateQuantity(item.medicine._id, item.quantity + 1)}
                                    sx={{ fontSize: '1.5rem' }}
                                  >
                                    +
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <TextField
                                  size="medium"
                                  type="number"
                                  value={item.costPrice}
                                  onChange={(e) => updateItem(item.medicine._id, 'costPrice', e.target.value)}
                                  sx={{ width: 100, fontSize: '1.1rem' }}
                                  // inputProps={{ 
                                  //   min: 0, 
                                  //   step: 0.01,
                                  //   style: { fontSize: '1.1rem' }
                                  // }}
                                />
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <TextField
                                  size="medium"
                                  type="number"
                                  value={item.sellingPrice}
                                  onChange={(e) => updateItem(item.medicine._id, 'sellingPrice', e.target.value)}
                                  sx={{ width: 100, fontSize: '1.1rem' }}
                                  // inputProps={{ 
                                  //   min: 0, 
                                  //   step: 0.01,
                                  //   style: { fontSize: '1.1rem' }
                                  // }}
                                />
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <TextField
                                  size="medium"
                                  type="number"
                                  value={item.mrp}
                                  onChange={(e) => updateItem(item.medicine._id, 'mrp', e.target.value)}
                                  sx={{ width: 100, fontSize: '1.1rem' }}
                                  // inputProps={{ 
                                  //   min: 0, 
                                  //   step: 0.01,
                                  //   style: { fontSize: '1.1rem' }
                                  // }}
                                />
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <TextField
                                  size="medium"
                                  type="number"
                                  value={item.taxRate}
                                  onChange={(e) => updateItem(item.medicine._id, 'taxRate', e.target.value)}
                                  sx={{ width: 90, fontSize: '1.1rem' }}
                                  // inputProps={{ 
                                  //   min: 0, 
                                  //   max: 100, 
                                  //   step: 0.01,
                                  //   style: { fontSize: '1.1rem' }
                                  // }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
                                  ₹{(item.quantity * item.costPrice).toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={{ py: 2 }}>
                                <IconButton
                                  color="error"
                                  onClick={() => removeItem(item.medicine._id)}
                                  size="large"
                                >
                                  <Delete sx={{ fontSize: 28 }} />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column - Order Summary */}
            <Grid item xs={12} md={4}>
              <Card sx={{ position: 'sticky', top: 100 }}>
                <CardContent sx={{ py: 3 }}>
                  <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                    Order Summary
                  </Typography>

                  {selectedSupplier && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                        Supplier
                      </Typography>
                      <Typography variant="h6" fontWeight="medium" sx={{ fontSize: '1.2rem' }}>
                        {selectedSupplier.name}
                      </Typography>
                    </Box>
                  )}

                  <TextField
                    fullWidth
                    type="date"
                    label="Expected Delivery"
                    value={expectedDelivery}
                    onChange={(e) => setExpectedDelivery(e.target.value)}
                    InputLabelProps={{ shrink: true, sx: { fontSize: '1.1rem' } }}
                    sx={{ mb: 2, fontSize: '1.1rem' }}
                    InputProps={{ sx: { fontSize: '1.1rem' } }}
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={{ mb: 2, fontSize: '1.1rem' }}
                    InputProps={{ sx: { fontSize: '1.1rem' } }}
                    InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                  />

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Subtotal:</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>₹{subtotal.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>Tax:</Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>₹{tax.toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>Total:</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary.main" sx={{ fontSize: '1.3rem' }}>
                        ₹{grandTotal.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  <Alert severity="info" sx={{ 
                    mb: 2,
                    fontSize: '1.1rem',
                    '& .MuiAlert-message': { fontSize: '1.1rem' }
                  }}>
                    This order will be created as a draft. You can send it to the supplier later.
                  </Alert>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleCreateOrder}
                    disabled={!selectedSupplier || items.length === 0 || loading}
                    sx={{ 
                      py: 2, 
                      fontSize: '1.2rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={28} color="inherit" />
                    ) : (
                      `Create Purchase Order - ₹${grandTotal.toFixed(2)}`
                    )}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};